import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import {
  BehaviorSubject,
  combineLatest,
  NEVER,
  Observable,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { ButtonTheme, IconPosition } from '@fcom/ui-components';
import { finShare, keepState } from '@fcom/rx';
import {
  Category,
  FinnairBoundItem,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerServiceItem,
  FinnairServiceCatalogCampaign,
  FinnairServiceCatalogCampaignCategory,
  FinnairServiceCatalogItemV3,
  FinnairServiceCatalogV3,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { campaignsShown, orderData, serviceCatalogCampaigns } from '@fcom/common-booking';
import { AppState, CmsDataService } from '@fcom/core';
import { CmsArticle } from '@fcom/core-api';
import { LanguageService } from '@fcom/ui-translate';
import { setCampaignShown } from '@fcom/common-booking/store/actions/services.actions';
import { unsubscribe } from '@fcom/core/utils';
import { combineServices } from '@fcom/common-booking/modules/ancillaries/utils';
import { getLowestPrice, isFlight, shouldWeUpsell } from '@fcom/common-booking/utils/common-booking.utils';

const ANIMATION_DURATION_MS = 450;

interface CampaignAndArticle {
  campaign: FinnairServiceCatalogCampaign;
  article: CmsArticle;
}

@Component({
  selector: 'fin-ancillary-campaign',
  styleUrls: ['ancillary-campaign.component.scss'],
  templateUrl: 'ancillary-campaign.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AncillaryCampaignComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Output()
  ctaClick: EventEmitter<Category[]> = new EventEmitter();

  promoVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  promoVisibleDelayed$: Observable<boolean>;
  campaigns$: Observable<FinnairServiceCatalogCampaign[]>;
  campaignAndArticle$: Observable<CampaignAndArticle>;
  showCampaign$: Observable<boolean>;
  order$: Observable<FinnairOrder>;
  bounds$: Observable<FinnairBoundItem[]>;

  subscriptions = new Subscription();

  constructor(
    private store$: Store<AppState>,
    private languageService: LanguageService,
    private cmsDataService: CmsDataService
  ) {}

  ngOnInit(): void {
    this.order$ = this.store$.pipe(orderData()).pipe(finShare());
    this.bounds$ = this.order$.pipe(map(({ bounds }) => bounds));
    this.campaigns$ = this.store$.pipe(
      serviceCatalogCampaigns(),
      filter((campaigns) => campaigns?.length > 0)
    );

    this.campaignAndArticle$ = this.campaigns$.pipe(
      switchMap((campaigns) => {
        return this.languageService.translate('fragments.campaign').pipe(
          switchMap((campaignsInCms: Record<string, { url: string }>) => {
            const foundCmsCampaign = campaigns.find((c) => campaignsInCms?.[c.id]);
            if (foundCmsCampaign) {
              return this.cmsDataService.getFragmentJson<CmsArticle>(campaignsInCms[foundCmsCampaign.id].url).pipe(
                map((article) => ({
                  article,
                  campaign: foundCmsCampaign,
                })),
                withLatestFrom(this.store$.pipe(campaignsShown())),
                filter(
                  ([campaignAndArticle, campaignIdsShown]) => !campaignIdsShown.includes(campaignAndArticle.campaign.id)
                ),
                map(([campaignAndArticle]) => campaignAndArticle)
              );
            }
            return NEVER;
          })
        );
      }),
      tap((campaignAndArticle: CampaignAndArticle) =>
        this.store$.dispatch(setCampaignShown({ id: campaignAndArticle.campaign.id }))
      ),
      finShare()
    );

    this.subscriptions.add(this.campaignAndArticle$.pipe(map(() => true)).subscribe(this.promoVisible$));

    this.promoVisibleDelayed$ = this.promoVisible$.pipe(distinctUntilChanged(), keepState(ANIMATION_DURATION_MS));
    this.showCampaign$ = combineLatest([
      this.order$.pipe(map((order) => order.serviceCatalog)),
      this.bounds$,
      this.campaignAndArticle$.pipe(
        map(
          (campaignAndArticle) =>
            campaignAndArticle?.campaign?.categories?.length === 1 &&
            campaignAndArticle?.campaign?.categories.find((cat) => cat.id === Category.SEAT)
        )
      ),
      this.order$.pipe(
        map((order) =>
          combineServices([order?.services?.included || [], order?.services?.unpaid || []]).find(
            (s) => s.category === Category.SEAT
          )
        )
      ),
      this.order$.pipe(
        map((order) =>
          order.passengers.filter((p) => p.passengerTypeCode !== FinnairPassengerCode.INF).map((p) => p.id)
        )
      ),
    ]).pipe(
      map(([serviceCatalog, bounds, seatCampaign, seats, passengerIds]) => {
        if (seatCampaign) {
          const seatsCatalog = this.getSeatServiceCatalog(serviceCatalog);
          if (seatsCatalog) {
            return this.shouldShowCampaignForSeatCatalog(
              bounds,
              serviceCatalog,
              Object.keys(seatCampaign.fragments),
              seatsCatalog,
              seats,
              passengerIds
            );
          }
          return false;
        } else {
          return true;
        }
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  closeCampaign(): void {
    this.promoVisible$.next(false);
  }

  ctaClicked(categories: FinnairServiceCatalogCampaignCategory[]): void {
    this.ctaClick.emit(categories.map((c) => c.id));
  }

  private shouldShowCampaignForSeatCatalog(
    bounds: FinnairBoundItem[],
    serviceCatalog: FinnairServiceCatalogV3,
    campaignFragmentIds: string[],
    seatsCatalog: FinnairServiceCatalogItemV3,
    seats: FinnairServiceItem,
    passengerIds: string[]
  ): boolean {
    return !!bounds.find((bound) => {
      if (shouldWeUpsell(Category.SEAT, bound, serviceCatalog)) {
        return (
          !seats || this.passengerWithoutPaidSeatExists(seats, campaignFragmentIds, bound, seatsCatalog, passengerIds)
        );
      }
      return false;
    });
  }

  private allSeatsAssigned(seats: FinnairServiceItem, passengerIds: string[]): boolean {
    return seats.bounds.every((b) =>
      b.segments.every((s) => {
        const passengerSeatItemIdsForSegment = s.passengers?.map((p) => p.id) ?? [];
        return passengerIds.every((passengerId) => passengerSeatItemIdsForSegment.includes(passengerId));
      })
    );
  }

  private passengerWithoutPaidSeatExists(
    seats: FinnairServiceItem,
    campaignFragmentIds: string[],
    bound: FinnairBoundItem,
    seatsCatalog: FinnairServiceCatalogItemV3,
    passengerIds: string[]
  ): boolean {
    const flightIds: string[] = this.getFlightIdsForCampaign(campaignFragmentIds, bound);
    const lowestPrice = getLowestPrice(seatsCatalog.lowestPrice, flightIds);
    if (lowestPrice?.money && +lowestPrice.money.amount > 0) {
      const passengersForCampaign = this.findPassengersSeatItemsForCampaign(seats, campaignFragmentIds);
      return (
        !this.allSeatsAssigned(seats, passengerIds) || !!passengersForCampaign?.find((p) => this.hasNoPaidSeats(p))
      );
    }
    return false;
  }

  private findPassengersSeatItemsForCampaign(
    seats: FinnairServiceItem,
    campaignFragmentIds: string[]
  ): FinnairPassengerServiceItem[] {
    return (
      seats?.bounds?.flatMap((b) =>
        b.segments.filter((segment) => campaignFragmentIds.includes(segment.id)).flatMap((s) => s.passengers)
      ) ?? []
    );
  }

  private hasNoPaidSeats(passenger: FinnairPassengerServiceItem): boolean {
    return !!passenger.services.find((service) => parseFloat(service.unitPrice?.amount) === 0);
  }

  private getFlightIdsForCampaign(campaignFragmentIds: string[], bound: FinnairBoundItem): string[] {
    return bound.itinerary
      .filter(isFlight)
      .map((flight) => flight.id)
      .filter((flightId) => campaignFragmentIds.includes(flightId));
  }

  private getSeatServiceCatalog(serviceCatalog: FinnairServiceCatalogV3): FinnairServiceCatalogItemV3 {
    return serviceCatalog.categories?.find((item) => item.category === Category.SEAT);
  }
}
